import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog'
import { Button } from '../components/ui/button'
import { useEffect, useState } from 'react'
import { Endpoints } from '@/remote/endpoints'
import { usePromiseToast } from '../hooks/toast-promisse'
import { useRemoteClientGet } from '@/configs/http-client'
import { EmployeeData } from '../validators/employee-form-validator'
import { convertDateDisplayFormat } from '../utils/formatDateDisplay'
import { formatToBRL } from '../utils/formatCurrency'
import { formatCTS } from '../utils/formatDocuments'

type DialogProps = {
  id: number
  isOpen: boolean
  closeDialog: () => void
}

export function DialogDetailEmployee({ id, isOpen = false, closeDialog }: DialogProps) {
  const { remoteClientGet } = useRemoteClientGet();
  const { triggerToast } = usePromiseToast();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<EmployeeData>();

  const employeeFields = [
    { label: 'Nome', value: data?.nome },
    { label: 'CPF', value: data?.cpf },
    { label: 'Data de Nascimento', value: data?.dataNascimento, isDate: true },
    { label: 'RG', value: data?.rg },
    { label: 'CTS', value: formatCTS(data?.ctps) },
    { label: 'Data de Admissão', value: data?.dataAdmissao, isDate: true },
    { label: 'Cargo', value: data?.cargo },
    { label: 'Salário', value: data?.salario, isCurrency: true },
    { label: 'Tipo Contrato', value: data?.tipoContrato },
    { label: 'Email', value: data?.contato?.email },
    { label: 'Telefone', value: data?.contato?.telefone },
    { label: 'Contato Emergência', value: data?.nomeEmergencia },
    { label: 'Telefone Emergência', value: data?.telefoneEmergencia }
  ];

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await remoteClientGet(`${Endpoints.employee}/${id}`);
      setData(response?.data?.data);
    } catch (error) {
      setError('Erro ao carregar dados...');
      console.error(error);
      throw error
    } finally {
      setIsLoading(false);
    }
  };

  const formatFieldValue = (field: { value: any, isDate?: boolean, isCurrency?: boolean }) => {
    if (!field.value) return "";
    if (field.isDate) return convertDateDisplayFormat(field.value.split('T')[0]);
    if (field.isCurrency) return formatToBRL(field.value);
    return field.value;
  };

  useEffect(() => {
    if (isOpen) {
      triggerToast(fetchData());
    }
  }, [isOpen, id]);

  const renderField = (field: any, index: number) => (
    <p key={index} className="text-gray-400 flex gap-1">
      <strong className="text-acelerafit-gray-dark">{field.label}:</strong>
      {formatFieldValue(field)}
    </p>
  );

  return (
    <Dialog open={isOpen}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader className="border-acelerafit-gray-dark">
          <DialogTitle className="text-acelerafit-green-300">Funcionário</DialogTitle>
          <DialogDescription>Detalhes</DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="flex justify-center items-center p-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-acelerafit-green-300" />
          </div>
        )}

        {error && (
          <div className="text-red-500 p-4 text-center">
            {error}
          </div>
        )}

        {!isLoading && !error && (
          <div className="grid grid-cols-2 col-span-1 gap-4">
            {employeeFields.map((field, index) => renderField(field, index))}
          </div>
        )}

        <DialogFooter className="sm:justify-end">
          <Button
            onClick={() => closeDialog()}
            className="bg-acelerafit-green-300 shadow-2xl hover:bg-acelerafit-green-300 hover:opacity-85 transition-all"
          >
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}